import type { ComponentPropsWithoutRef, ComponentType, Key } from 'react'
import { useSpring, useTrail, config, animated, useTransition } from 'react-spring'
import type { SpringConfig } from 'react-spring'

type FadeProps = ComponentPropsWithoutRef<'div'> & {
  show?: boolean
  delay?: number
  springConfig?: SpringConfig
  fromOpacity?: number
  onRest?: () => void
  // When false, the shown element does NOT capture pointer events, so clicks fall
  // through to whatever is behind it. Used by the no-backdrop Modal (overlay=false)
  // whose full-screen wrapper would otherwise block the page behind it (SCI-3741).
  interactive?: boolean
}

export const Fade = ({
  show,
  delay,
  children,
  className,
  springConfig,
  style = {},
  fromOpacity = 0,
  onRest,
  interactive = true,
  ...rest
}: FadeProps) => {
  const transition = useTransition(show, {
    from: { opacity: fromOpacity },
    // as const: the animated style prop only accepts real pointer-events keywords,
    // not a widened string (see FadeVisual below).
    enter: { opacity: 1, pointerEvents: interactive ? ('auto' as const) : ('none' as const) },
    leave: { opacity: 0, pointerEvents: 'none' },
    delay,
    onRest
  })

  return transition(
    (props, show) =>
      show && (
        <animated.div className={className} style={{ ...style, ...props }} {...rest}>
          {children}
        </animated.div>
      )
  )
}

interface FadeTrailItem {
  key: Key
  component: ComponentType<Record<string, unknown>>
  componentProps?: Record<string, unknown>
}

type FadeTrailProps = ComponentPropsWithoutRef<'div'> & {
  items: FadeTrailItem[]
  springConfig?: SpringConfig
}

export const FadeTrail = ({ items, springConfig, ...rest }: FadeTrailProps) => {
  const trail = useTrail(items.length, {
    config: springConfig || config.default,
    from: { opacity: 0 },
    opacity: 1
  })
  return trail.map((style, index) => {
    const item = items[index]
    return (
      <animated.div key={items[index].key} style={style} {...rest}>
        <item.component {...item.componentProps} />
      </animated.div>
    )
  })
}

type FadeVisualProps = ComponentPropsWithoutRef<'div'> & {
  show?: boolean
  onRest?: () => void
}

export const FadeVisual = ({ className, children, show, onRest, ...rest }: FadeVisualProps) => {
  const props = useSpring({
    opacity: show ? 1 : 0,
    // Asserted literal, not a widened string: the animated style prop only accepts
    // a FluidValue of real pointer-events keywords.
    pointerEvents: show ? ('auto' as const) : ('none' as const),
    onRest
  })

  return (
    <animated.div className={className} style={props} {...rest}>
      {children}
    </animated.div>
  )
}

export default Fade
